import { APP_ROUTE_PATHS, replaceParam } from '@ps/core-config';

/**
 * Bidirectional route map for the Legacy <-> Phoenix UI switcher (PD UI
 * Switcher project). Each key represents one "intent" (e.g. "I'm looking at
 * a survey's manage view") that exists as a distinct route in both apps.
 *
 * Phoenix templates are pulled straight from `APP_ROUTE_PATHS`, the single
 * source of truth for this app's routes. Legacy has no equivalent shared
 * package, so its templates are hardcoded here and must be kept in sync by
 * hand, each one is commented with where it's registered in `platform-ui`.
 *
 * All templates are bare path segments (no leading slash), Angular-style
 * params (`:id`), matching the convention in `app-routes.constants.ts`.
 */
export const PLATFORM_SWITCHER_ROUTE_KEYS = [
  'dashboard',
  'createSurvey',
  'editSurvey',
  'manageSurvey',
  'launchSurvey',
] as const;

export type PlatformSwitcherRouteKey =
  (typeof PLATFORM_SWITCHER_ROUTE_KEYS)[number];

interface PlatformSwitcherRouteTemplate {
  phoenix: string;
  legacy: string;
}

export const PLATFORM_SWITCHER_ROUTE_MAP: Record<
  PlatformSwitcherRouteKey,
  PlatformSwitcherRouteTemplate
> = {
  // platform-ui: src/app/dashboard/dashboard.module.ts -> { path: 'dashboard', children: [{ path: '' }] }
  dashboard: {
    phoenix: APP_ROUTE_PATHS.dashboard,
    legacy: 'dashboard',
  },
  // platform-ui: src/app/create-surveys/create-survey.module.ts -> { path: 'create-surveys' }
  createSurvey: {
    phoenix: APP_ROUTE_PATHS.createSurvey,
    legacy: 'create-surveys',
  },
  // platform-ui: src/app/create-surveys/create-survey.module.ts -> { path: 'surveys', children: [{ path: ':id/edit' }] }
  // Note: this is NOT under `create-surveys` in Legacy, despite the create
  // flow living in the same module/component.
  editSurvey: {
    phoenix: APP_ROUTE_PATHS.editSurvey,
    legacy: 'surveys/:id/edit',
  },
  // platform-ui: src/app/dashboard/dashboard.module.ts -> { path: 'dashboard', children: [{ path: ':id' }] }
  manageSurvey: {
    phoenix: APP_ROUTE_PATHS.manageSurvey,
    legacy: 'dashboard/:id',
  },
  // platform-ui: src/app/buyer/buyer.module.ts -> { path: 'launch-survey/:id' }
  launchSurvey: {
    phoenix: APP_ROUTE_PATHS.launchSurvey,
    legacy: 'launch-survey/:id',
  },
};

function interpolate(
  template: string,
  params: Record<string, string | number>,
): string {
  return Object.entries(params).reduce(
    (path, [param, value]) => replaceParam(path, param, value),
    template,
  );
}

/**
 * Builds the Legacy path (with a leading slash) equivalent to the given
 * Phoenix route intent, interpolating any route params (e.g. `{ id: 123 }`).
 */
export function toLegacyPath(
  routeKey: PlatformSwitcherRouteKey,
  params: Record<string, string | number> = {},
): string {
  return `/${interpolate(PLATFORM_SWITCHER_ROUTE_MAP[routeKey].legacy, params)}`;
}

/**
 * Builds the Phoenix path (with a leading slash) equivalent to the given
 * route intent, interpolating any route params (e.g. `{ id: 123 }`).
 */
export function toPhoenixPath(
  routeKey: PlatformSwitcherRouteKey,
  params: Record<string, string | number> = {},
): string {
  return `/${interpolate(PLATFORM_SWITCHER_ROUTE_MAP[routeKey].phoenix, params)}`;
}

function toRegex(template: string): RegExp {
  const pattern = template.replace(/:([^/]+)/g, '(?<$1>[^/]+)');
  return new RegExp(`^/${pattern}/?$`);
}

export interface PlatformSwitcherRouteMatch {
  routeKey: PlatformSwitcherRouteKey;
  params: Record<string, string>;
}

/**
 * Reverse lookup: given the current Phoenix URL (path, optionally with query
 * string and/or fragment, e.g. straight from the router's `url`), finds which
 * switcher intent it maps to (and its params), for a navbar-level button that
 * has no other way of knowing what page it's on. Literal routes (e.g.
 * `surveys/new`) must come before their param siblings (`surveys/:id`) in
 * `PLATFORM_SWITCHER_ROUTE_KEYS`, that ordering is what keeps `:id` from
 * swallowing a literal segment.
 */
export function matchPhoenixPath(
  url: string,
): PlatformSwitcherRouteMatch | null {
  const pathname = url.split('?')[0].split('#')[0];

  for (const routeKey of PLATFORM_SWITCHER_ROUTE_KEYS) {
    const match = toRegex(PLATFORM_SWITCHER_ROUTE_MAP[routeKey].phoenix).exec(
      pathname,
    );
    if (match) {
      return { routeKey, params: match.groups ?? {} };
    }
  }
  return null;
}
